import {
    type BaseModel,
    ElementTypes,
    getRunsForExport,
    type ImageModel,
    isLockedRecursively,
    type MultiPageImageModel,
    MultiPageVideoModel,
    SpecificationParser,
    type Template,
    type TemplateElement,
    TextElement,
    type UpdateGlobalPropertyOptions,
    VideoModel,
    type VisualElement,
    WebSpecificationParser,
} from '@bynder-studio/render-core';
import { getStructuredText } from '@bynder-studio/render-web';
import { cleanS3Url } from '@bynder-studio/misc';
import { clone, equals } from 'rambda';
import { flattenTree, getElementType } from 'packages/common/editor/helpers/elementtree';
import { Page } from 'packages/pages/design/VariationsContext/types';

const EMPTY_AUDIO_PARAMS = {
    src: null,
    srcType: null,
    srcId: null,
    fileName: '',
    offsetTime: 0,
};

const EMPTY_AUDIO_ASSET_VALUE = {
    type: 'ASSET_MANAGER',
    value: null,
    offsetTime: 0,
    fileName: '',
    fileUrl: null,
    deleted: false,
};

// Limited list of properties that can be changed and saved in CC and SHOULD NOT BE RENAMED
const COMMON_CHANGEABLE_PROPS = ['hidden', 'duration', 'startFrame', 'contentTransform', 'renderOrder', 'useAudio'];
const TEXT_CHANGEABLE_PROPS = ['textDirection', 'textBackground'];
const SHAPE_CHANGEABLE_PROPS = ['fillColor', 'borderColor'];
const PLAYBACK_CHANGEABLE_PROPS = ['gain', 'fadeIn', 'fadeOut', 'offsetTime'];
const GROUP_CHANGEABLE_PROPS = ['activeDropdownElementId'];
const TRANSCRIPT_CHANGEABLE_PROPS = ['enabled'];

const ALL_CHANGEABLE_PROPS = [
    ...COMMON_CHANGEABLE_PROPS,
    ...TEXT_CHANGEABLE_PROPS,
    ...SHAPE_CHANGEABLE_PROPS,
    ...PLAYBACK_CHANGEABLE_PROPS,
    ...GROUP_CHANGEABLE_PROPS,
    ...TRANSCRIPT_CHANGEABLE_PROPS,
];

const isPropNameValid = (propName: string) => ALL_CHANGEABLE_PROPS.includes(propName);

type VariationTemplateElement = {
    type?: string;
    properties?: {
        onlyAllowOneSelection?: boolean;
        renderOrder?: number;
    };
    children?: VariationTemplateElement[] | Record<string, VariationTemplateElement>;
};

type VariationTemplateElementCollection = VariationTemplateElement[] | Record<string, VariationTemplateElement>;

type VariationTemplatePage = {
    elements?: VariationTemplateElementCollection;
    globalElements?: VariationTemplateElementCollection;
};

const getVariationTemplateRenderOrder = (element: VariationTemplateElement) => element.properties?.renderOrder ?? 0;

const mapVariationTemplateElementCollection = <T extends VariationTemplateElementCollection>(
    elements: T,
    mapFn: (element: VariationTemplateElement) => VariationTemplateElement,
): T => {
    if (Array.isArray(elements)) {
        return elements.map(mapFn) as T;
    }

    return Object.fromEntries(Object.entries(elements).map(([key, element]) => [key, mapFn(element)])) as T;
};

const normalizeVariationTemplateDesElementRenderOrders = (
    element: VariationTemplateElement,
): VariationTemplateElement => {
    if (!element.children || Object.keys(element.children).length === 0) {
        return element;
    }

    const isDesGroup = element.type === ElementTypes.GROUP && Boolean(element.properties?.onlyAllowOneSelection);
    const children = isDesGroup
        ? normalizeVariationTemplateDesChildrenRenderOrders(element.children, getVariationTemplateRenderOrder(element))
        : mapVariationTemplateElementCollection(element.children, normalizeVariationTemplateDesElementRenderOrders);

    return {
        ...element,
        children,
    };
};

const normalizeVariationTemplateDesChildrenRenderOrders = (
    children: VariationTemplateElementCollection,
    parentRenderOrder: number,
) => {
    const normalizedChildren: VariationTemplateElementCollection = Array.isArray(children)
        ? [...children]
        : { ...children };

    const sortedChildren = Object.entries(children)
        .map(([key, child], index) => ({ key, child, index }))
        .sort((childA, childB) => {
            const renderOrderDiff =
                getVariationTemplateRenderOrder(childA.child) - getVariationTemplateRenderOrder(childB.child);

            if (renderOrderDiff !== 0) {
                return renderOrderDiff;
            }

            return childA.index - childB.index;
        });

    let nextRenderOrder = parentRenderOrder + 1;

    sortedChildren.forEach(({ key }) => {
        if (Array.isArray(normalizedChildren)) {
            const childIndex = Number(key);
            const child = normalizedChildren[childIndex];

            normalizedChildren[childIndex] = {
                ...child,
                properties: {
                    ...child.properties,
                    renderOrder: nextRenderOrder,
                },
            };
        } else {
            const child = normalizedChildren[key];

            normalizedChildren[key] = {
                ...child,
                properties: {
                    ...child.properties,
                    renderOrder: nextRenderOrder,
                },
            };
        }

        nextRenderOrder += 1;
    });

    return mapVariationTemplateElementCollection(normalizedChildren, normalizeVariationTemplateDesElementRenderOrders);
};

// Variation-only helper: normalizes DES child renderOrders before template/variation reconciliation.
export const normalizeVariationTemplateDesRenderOrders = <T extends { pages?: VariationTemplatePage[] }>(
    template: T,
): T => {
    if (!template?.pages) {
        return template;
    }

    return {
        ...template,
        pages: template.pages.map((page) => ({
            ...page,
            elements: page.elements
                ? mapVariationTemplateElementCollection(page.elements, normalizeVariationTemplateDesElementRenderOrders)
                : page.elements,
            globalElements: page.globalElements
                ? mapVariationTemplateElementCollection(
                      page.globalElements,
                      normalizeVariationTemplateDesElementRenderOrders,
                  )
                : page.globalElements,
        })),
    };
};

export const isVariationInvalid = (size) => size.hasDeletedAsset || size.failureReason || size.hasOversetText;

export const hasVariationErrors = (
    { id, sizes }: { id: string; sizes: Record<string, Page> },
    oversetVariations: Set<string>,
) => oversetVariations.has(id) || Object.values(sizes).some((size) => isVariationInvalid(size));

/**
 * Inspect what a variation can be translated: whether it has editable (non-locked)
 * text elements and whether it has an enabled transcript. `isTranslateDisabled` is a
 * convenience flag that is true when neither is translatable.
 *
 * Shared between the variations list (to disable the "Translate" action) and the
 * translate modal (to toggle option visibility, defaults, and the submit button).
 */
export const getVariationTranslationCapabilities = (
    creativeModel: MultiPageVideoModel | MultiPageImageModel | null | undefined,
) => {
    if (!creativeModel) {
        return { hasEditableTextElements: false, hasTranscript: false, isTranslateDisabled: true };
    }

    const models = creativeModel.getModels();

    const textElements = models.flatMap((model: ImageModel | VideoModel) =>
        model.getAllElementsRecursively<VisualElement>('visual').filter((el) => el instanceof TextElement),
    );

    const hasEditableTextElements = textElements.length > 0 && !textElements.every((el) => isLockedRecursively(el));

    const hasTranscript = models.some(
        (model: ImageModel | VideoModel) => model instanceof VideoModel && model.getTranscriptElement()?.enabled,
    );

    const isTranslateDisabled = !hasEditableTextElements && !hasTranscript;

    return { hasEditableTextElements, hasTranscript, isTranslateDisabled };
};

export const cleanUpNewValues = (values: { [key: string]: any }) => {
    const temp = { ...values };

    Object.keys(temp).forEach((propName) => {
        if (TextElement.servicePropsList.includes(propName)) {
            delete temp[propName];
        }
    });

    return temp;
};

const stripTranscriptCueId = ({ id, ...cue }: { id?: string; [key: string]: unknown }) => cue;

const filterPropertyValue = (type: ElementTypes, name: string, value) => {
    if (name === 'value') {
        switch (type) {
            case ElementTypes.TEXT:
                return { text: value };
            case ElementTypes.IMAGE:
                if (!value.thumbnail || value.deleted) {
                    return null;
                }

                return {
                    src: value.thumbnail,
                    srcId: value.value,
                    fileName: value.fileName,
                    srcType: value.type,
                    naturalDimension: { width: value.naturalWidth, height: value.naturalHeight },
                };
            case ElementTypes.VIDEO:
                if (!value || value.deleted) {
                    return null;
                }

                return {
                    src: value.videoPreviewUrl,
                    srcId: value.value,
                    fileName: value.fileName,
                    thumbnail: value.thumbnail,
                    naturalDimension: { width: value.naturalWidth, height: value.naturalHeight },
                    isAlpha: value.isAlpha,
                    offsetTime: value.offsetTime,
                };
            case ElementTypes.AUDIO:
                if (!value || value.deleted) {
                    return null;
                }

                return {
                    src: value.fileUrl,
                    srcId: value.value,
                    fileName: value.fileName,
                    offsetTime: value.offsetTime,
                };
            case ElementTypes.GLOBAL_AUDIO:
                if (!value) {
                    return null;
                }

                return {
                    src: value.fileUrl,
                    srcType: 'ASSET_MANAGER',
                    srcId: value.value,
                    fileName: value.fileName,
                    offsetTime: value.offsetTime,
                };
            case ElementTypes.BACKGROUND_COLOR:
                return {
                    color: value,
                };
            case ElementTypes.POSTER_FRAME:
                return {
                    frame: value,
                };
            case ElementTypes.TRANSCRIPT:
                return {
                    cues: WebSpecificationParser.parseCuesFromEntries(value?.entries),
                };
            default:
                return null;
        }
    }

    if (ALL_CHANGEABLE_PROPS.includes(name)) {
        return { [name]: value };
    }

    return null;
};

const getCreativeModelElements = (creativeModel: ImageModel | VideoModel | MultiPageVideoModel | MultiPageImageModel) =>
    creativeModel.getAllElementsRecursively().reduce((acc, el) => {
        const serializedElement = el.toObject() as Record<string, unknown>;

        acc[el.id] = {
            ...serializedElement,
            type: serializedElement.type || (el as { type?: ElementTypes }).type || getElementType(el),
        };

        return acc;
    }, {});

export const getGlobalProperties = (
    creativeModel: ImageModel | VideoModel | MultiPageVideoModel | MultiPageImageModel,
) => {
    const globalProperties = {};

    if (creativeModel instanceof MultiPageVideoModel || creativeModel instanceof VideoModel) {
        if (creativeModel.getGlobalAudioTrack1) {
            const audio = creativeModel.getGlobalAudioTrack1();
            globalProperties[audio.id] = {
                ...audio,
                type: ElementTypes.GLOBAL_AUDIO,
            };
        }

        if (creativeModel.getGlobalAudioTrack2) {
            const audio = creativeModel.getGlobalAudioTrack2();
            globalProperties[audio.id] = {
                ...audio,
                type: ElementTypes.GLOBAL_AUDIO,
            };
        }

        if (creativeModel.getPosterFrame) {
            const posterFrame = creativeModel.getPosterFrame();
            globalProperties[posterFrame.id] = {
                ...posterFrame,
                type: ElementTypes.POSTER_FRAME,
            };
        }
    }

    if (creativeModel.getBackgroundColor) {
        const backgroundColor = creativeModel.getBackgroundColor();
        globalProperties[backgroundColor.id] = {
            ...backgroundColor,
            type: ElementTypes.BACKGROUND_COLOR,
        };
    }

    if (creativeModel instanceof MultiPageVideoModel || creativeModel instanceof VideoModel) {
        if (creativeModel.getTranscriptElement) {
            const transcript = creativeModel.getTranscriptElement();

            globalProperties[transcript.id] = {
                ...transcript,
                type: ElementTypes.TRANSCRIPT,
            };
        }
    }

    return globalProperties;
};

export const parseVariation = ({
    entries,
    creativeModel,
    template,
}: {
    entries: Page[];
    creativeModel: MultiPageImageModel | MultiPageVideoModel;
    template: Template;
}) => {
    const allModels = creativeModel.getModels();
    const allModelsMetaData = creativeModel.getModelsMetaData();

    return entries.reduce((acc, item) => {
        const { creativeVersionPageId: sizeId, variationId, properties, ...params } = item;

        const pageModelIdx = allModelsMetaData.findIndex((model) => model.id.toString() === sizeId.toString());
        const pageModel = allModels[pageModelIdx];
        const templateEls = {
            ...getCreativeModelElements(pageModel),
            ...getGlobalProperties(pageModel),
        };
        const elements = {};

        properties.forEach(({ elementProperty: prop, value: propValue }) => {
            // check if audio asset is removed from variation
            let resolvedValue = propValue;

            if (prop?.type === 'VALUE_SOURCE' && !resolvedValue) {
                resolvedValue = EMPTY_AUDIO_ASSET_VALUE;
            } else if (resolvedValue === null || resolvedValue === undefined) {
                return;
            }

            const { templateElementId: id, name } = prop;

            // TODO: Delete after BE migration!
            if (name === 'renderOrder') {
                return;
            }

            if (id in templateEls) {
                const { type } = templateEls[id];
                const filteredValue = filterPropertyValue(type, name, resolvedValue);

                if (!filteredValue) {
                    return;
                }

                if (type === ElementTypes.TEXT && filteredValue.text !== undefined && filteredValue.text !== null) {
                    const defaultprops = pageModel.getElementById<TextElement>(id).getTextProps().runs[0];
                    // @ts-ignore
                    filteredValue.formattedText = getStructuredText(filteredValue.text, defaultprops);
                    // TODO: Seems useless, delete later
                    // delete filteredValue.text;
                }

                if (id in elements) {
                    elements[id] = { ...elements[id], ...filteredValue };
                } else {
                    elements[id] = filteredValue;
                }
            }
        });

        const mergedElements = mergeTemplateWithVariation({ template, pageId: sizeId, elements });
        const globalElements = Object.fromEntries(Object.entries(elements).filter(([id]) => !(id in mergedElements)));

        acc[sizeId] = {
            ...params,
            sizeId,
            properties,
            variationId,
            elements: {
                ...mergedElements,
                ...globalElements,
            },
        };

        return acc;
    }, {});
};

const getElementPropertyValue = (elementProps, elementType, propName) => {
    if (['TEXT_STYLES', 'BRAND_COLORS'].includes(elementType)) {
        return;
    }

    if (propName === 'value') {
        switch (elementType) {
            case ElementTypes.TEXT: {
                const { value, runs, layoutRuns } = elementProps.formattedText;

                return {
                    value,
                    layoutRuns,
                    runs: getRunsForExport(runs),
                };
            }
            case ElementTypes.IMAGE:
            case ElementTypes.VIDEO:
            case ElementTypes.AUDIO:
                return {
                    type: 'ASSET_MANAGER',
                    value: elementProps.srcId,
                    offsetTime: elementProps.offsetTime,
                    thumbnailStorageUrl: null,
                };
            case ElementTypes.GLOBAL_AUDIO:
                if (!elementProps.srcId) {
                    return null;
                }

                return {
                    type: 'ASSET_MANAGER',
                    value: elementProps.srcId,
                    offsetTime: elementProps.offsetTime,
                };
            case ElementTypes.BACKGROUND_COLOR:
                return elementProps.color;
            case ElementTypes.POSTER_FRAME:
                return elementProps.frame;
            case ElementTypes.TRANSCRIPT:
                // An empty (but present) cues array means the user cleared all cues via "clear all";
                // we must persist `{ entries: [] }` so the cleared state is saved to the variation.
                // Only bail out with `undefined` when there is no cues property to save at all.
                return Array.isArray(elementProps.cues)
                    ? { entries: elementProps.cues.map(stripTranscriptCueId) }
                    : undefined;
            default:
                return;
        }
        // TODO: Temporary solution until backend implements consistent behaviour for all properties
        // https://bynder.atlassian.net/browse/VBS-22156
    } else if (propName === 'offsetTime') {
        switch (elementType) {
            case ElementTypes.VIDEO:
            case ElementTypes.AUDIO:
                return {
                    type: 'ASSET_MANAGER',
                    value: elementProps.srcId,
                    offsetTime: elementProps.offsetTime,
                    thumbnailStorageUrl: null,
                };

            case ElementTypes.GLOBAL_AUDIO:
                if (!elementProps.srcId) {
                    return null;
                }

                return {
                    type: 'ASSET_MANAGER',
                    value: elementProps.srcId,
                    offsetTime: elementProps.offsetTime,
                };

            default:
                return;
        }
    }

    if (propName in elementProps) {
        return elementProps[propName];
    }
};

const getPropNameForSaving = (propertyName: string, elementType: string) => {
    if (isPropNameValid(propertyName)) {
        return propertyName;
    }

    switch (elementType) {
        case ElementTypes.TEXT: {
            return propertyName === 'formattedText' ? 'value' : '';
        }
        case ElementTypes.IMAGE:
        case ElementTypes.VIDEO:
        case ElementTypes.AUDIO:
        case ElementTypes.GLOBAL_AUDIO: {
            return propertyName === 'srcId' ? 'value' : '';
        }
        case ElementTypes.BACKGROUND_COLOR: {
            return propertyName === 'color' ? 'value' : '';
        }
        case ElementTypes.TRANSCRIPT: {
            return propertyName === 'cues' ? 'value' : '';
        }

        default:
            return '';
    }
};

// `getPropNameForSaving` is N→1 (e.g., both `srcId` and `offsetTime` save under BE name `value`
// for VIDEO/AUDIO/GLOBAL_AUDIO), so the inverse can fan out into multiple model field names.
const parsePropNames = (propertyName: string, elementType: string): string[] => {
    if (isPropNameValid(propertyName)) {
        return [propertyName];
    }

    switch (elementType) {
        case ElementTypes.TEXT: {
            return propertyName === 'value' ? ['formattedText'] : [];
        }
        case ElementTypes.IMAGE: {
            return propertyName === 'value' ? ['srcId'] : [];
        }
        case ElementTypes.VIDEO:
        case ElementTypes.AUDIO:
        case ElementTypes.GLOBAL_AUDIO: {
            return propertyName === 'value' ? ['srcId', 'offsetTime'] : [];
        }
        case ElementTypes.BACKGROUND_COLOR: {
            return propertyName === 'value' ? ['color'] : [];
        }
        case ElementTypes.TRANSCRIPT: {
            if (propertyName === 'value') {
                return ['cues'];
            }

            return TRANSCRIPT_CHANGEABLE_PROPS.includes(propertyName) ? [propertyName] : [];
        }
        default:
            return [];
    }
};

const getGlobalElementByType = (type: string, pageModel?: VideoModel | ImageModel, id?: string) => {
    if (!pageModel) {
        return null;
    }

    if (type === ElementTypes.GLOBAL_AUDIO) {
        const videoModel = pageModel as VideoModel;

        return [videoModel.getGlobalAudioTrack1(), videoModel.getGlobalAudioTrack2()].find(
            (audio) => audio.id === Number(id),
        );
    }

    if (type === ElementTypes.BACKGROUND_COLOR) {
        return pageModel.getBackgroundColor();
    }

    if (type === ElementTypes.POSTER_FRAME && 'getPosterFrame' in pageModel) {
        return (pageModel as VideoModel).getPosterFrame();
    }

    if (type === ElementTypes.TRANSCRIPT && 'getTranscriptElement' in pageModel) {
        return (pageModel as VideoModel).getTranscriptElement();
    }

    return null;
};

export const prepareVariationToSave = ({
    template,
    variation,
    creativeModel,
    resetPages = [],
}: {
    template: any;
    variation: any;
    creativeModel: MultiPageImageModel | MultiPageVideoModel;
    resetPages?: string[];
}) => {
    const newValues = variation.newValues || {};

    return Object.keys(variation.sizes).reduce((acc, pageId) => {
        if (resetPages.includes(pageId)) {
            return acc;
        }

        const page = variation.sizes[pageId];
        const pageModel = creativeModel.getModelByPageId(pageId.toString());
        const templateEls = {
            ...getCreativeModelElements(pageModel),
            ...getGlobalProperties(pageModel),
        };
        const savedProps = page.properties.reduce((accum, current) => {
            const {
                elementProperty: { templateElementId, name },
            } = current;

            // TODO: Delete after BE migration!
            if (name === 'renderOrder') {
                return accum;
            }

            if (!accum[templateElementId]) {
                accum[templateElementId] = new Set();
            }

            parsePropNames(name, templateEls[templateElementId].type).forEach((p) => accum[templateElementId].add(p));

            return accum;
        }, {});

        const allAffectedIds = new Set([...(Object.keys(newValues?.[pageId] || {}) || []), ...Object.keys(savedProps)]);
        const affectedElements = [...allAffectedIds].reduce((accum, current) => {
            if (!accum[current]) {
                accum[current] = new Set();
            }

            accum[current] = new Set([...(newValues?.[pageId]?.[current] || []), ...(savedProps?.[current] || [])]);

            return accum;
        }, {});

        Object.keys(affectedElements).forEach((elId) => {
            const changedProps = affectedElements[elId];

            changedProps.forEach((propName) => {
                const type = templateEls?.[elId]?.type;

                if (!type) {
                    return;
                }

                const name = getPropNameForSaving(propName, type);
                const { elements } = page;

                if (!(elId in elements) || !name) {
                    return;
                }

                const value = getElementPropertyValue(elements[elId], type, name);

                if (value || value !== undefined) {
                    const desiredPage = template.pages.find((page) => page.id?.toString() === pageId.toString());
                    const desiredPageElements = flattenTree(desiredPage?.elements || [], true) as Record<
                        string,
                        TemplateElement
                    >;
                    const desiredPageGlobalElements = Array.isArray(desiredPage?.globalElements)
                        ? desiredPage.globalElements
                        : Object.values(desiredPage?.globalElements || {});
                    const templateGlobalEl = desiredPageGlobalElements.find(
                        (el: any) => el?.id?.toString() === elId.toString(),
                    ) as TemplateElement | undefined;
                    const templateEl: TemplateElement | undefined = desiredPageElements[elId] || templateGlobalEl;
                    const globalElementType = templateGlobalEl?.type;
                    const globalElement = getGlobalElementByType(globalElementType, pageModel, elId);
                    const currentElement = globalElementType ? globalElement : pageModel.getElementById(Number(elId));

                    if (!currentElement || !templateEl) {
                        return;
                    }

                    const elementType = globalElementType || templateEl.type || getElementType(currentElement as any);
                    const { tmpl, prop } = getComparableObj(elementType, currentElement, templateEl);
                    const isChanged = !equals(tmpl?.[propName], prop?.[propName]);

                    if (!isChanged) {
                        return;
                    }

                    if (!acc[pageId]) {
                        acc[pageId] = [];
                    }

                    // TODO: Temporary solution until backend implements consistent behaviour for all properties
                    // https://bynder.atlassian.net/browse/VBS-22156
                    const getName = (n) => (n === 'offsetTime' ? 'value' : n);

                    acc[pageId].push({ templateId: elId, name: getName(name), value });
                }
            });
        });

        return acc;
    }, {});
};

const mergeTemplateWithVariation = ({
    template,
    pageId,
    elements,
}: {
    template: { pages: any[] };
    pageId: number;
    elements: { [key: string]: any };
}) => {
    const page = template.pages.find((page) => Number(page.id) === pageId);

    const getDefinedValue = (value: any, defaultValue: any) => (value === undefined ? defaultValue : value);

    const getMergedMediaContentTransform = (
        variationContentTransform,
        templateContentTransform,
        mediaNaturalDimension,
        templateElement,
        isMediaChanged = false,
    ) => {
        if (variationContentTransform) {
            return variationContentTransform;
        }

        if (templateContentTransform === null || templateContentTransform === undefined) {
            return templateContentTransform;
        }

        // Only strip scale when the variation actually imported new media.
        // If the media hasn't changed, preserve the template's contentTransform as-is.
        if (!isMediaChanged) {
            return templateContentTransform;
        }

        // Imported variation media should default to auto-derived scale for non-FREE modes.
        if (templateContentTransform.type !== 'FREE') {
            const { scale, ...transformWithoutScale } = templateContentTransform;

            return transformWithoutScale;
        }

        const boxWidth = templateElement?.dimension?.width ?? templateElement?.properties?.width;
        const boxHeight = templateElement?.dimension?.height ?? templateElement?.properties?.height;
        const naturalWidth = mediaNaturalDimension?.width;
        const naturalHeight = mediaNaturalDimension?.height;

        if (!boxWidth || !boxHeight || !naturalWidth || !naturalHeight) {
            return templateContentTransform;
        }

        const scale = Number(Math.min(boxWidth / naturalWidth, boxHeight / naturalHeight, 1).toFixed(2));

        return {
            ...templateContentTransform,
            scale,
            horizontalOffset: Math.round(boxWidth / 2 - (naturalWidth * scale) / 2),
            verticalOffset: Math.round(boxHeight / 2 - (naturalHeight * scale) / 2),
        };
    };

    return Object.values(flattenTree(page?.elements || [], true) as Record<string, TemplateElement>).reduce(
        (accum, tmplEl) => {
            const variationData = elements?.[tmplEl.id] || {};

            switch (tmplEl.type) {
                case ElementTypes.TEXT: {
                    const defaultProps = SpecificationParser.getTextDefaultProps(tmplEl);
                    accum[tmplEl.id] = {
                        ...variationData,
                        startFrame: variationData?.startFrame ?? (tmplEl.properties.startFrame || 0),
                        duration: variationData?.duration ?? (tmplEl.properties.duration || 1),
                        hidden: variationData?.hidden ?? (tmplEl.properties?.hidden || false),
                        formattedText: getStructuredText(
                            variationData?.formattedText ?? (tmplEl.properties?.value || ''),
                            defaultProps,
                        ),
                        textDirection: variationData?.textDirection ?? (tmplEl.properties?.textDirection || null),
                        textBackground: variationData?.textBackground ?? (tmplEl.properties?.textBackground || null),
                        contentTransform: variationData.contentTransform ?? tmplEl.properties?.contentTransform,
                    };
                    break;
                }

                case ElementTypes.IMAGE: {
                    accum[tmplEl.id] = {
                        ...variationData,
                        startFrame: variationData?.startFrame ?? (tmplEl.properties.startFrame || 0),
                        duration: variationData?.duration ?? (tmplEl.properties.duration || 1),
                        hidden: variationData?.hidden ?? (tmplEl.properties?.hidden || false),
                        src: getDefinedValue(variationData?.src, tmplEl.properties?.value?.thumbnail),
                        srcId: getDefinedValue(variationData?.srcId, tmplEl.properties?.value?.value),
                        srcType: getDefinedValue(variationData?.srcType, tmplEl.properties?.value?.type),
                        fileName: getDefinedValue(variationData?.fileName, tmplEl.properties?.fileName || ''),
                        naturalDimension: {
                            width: variationData?.naturalDimension?.width ?? tmplEl.properties?.naturalWidth,
                            height: variationData?.naturalDimension?.height ?? tmplEl.properties?.naturalHeight,
                        },
                        contentTransform: getMergedMediaContentTransform(
                            variationData.contentTransform,
                            tmplEl.properties?.contentTransform,
                            {
                                width: variationData?.naturalDimension?.width ?? tmplEl.properties?.naturalWidth,
                                height: variationData?.naturalDimension?.height ?? tmplEl.properties?.naturalHeight,
                            },
                            tmplEl,
                            variationData?.srcId !== undefined,
                        ),
                    };
                    break;
                }

                case ElementTypes.VIDEO: {
                    accum[tmplEl.id] = {
                        ...variationData,
                        startFrame: variationData?.startFrame ?? (tmplEl.properties.startFrame || 0),
                        duration: variationData?.duration ?? (tmplEl.properties.duration || 1),
                        hidden: variationData?.hidden ?? (tmplEl.properties?.hidden || false),
                        src: getDefinedValue(variationData?.src, tmplEl.properties?.value?.videoPreviewUrl),
                        srcId: getDefinedValue(variationData?.srcId, tmplEl.properties?.value?.value),
                        srcType: getDefinedValue(variationData?.srcType, tmplEl.properties?.value?.type),
                        fileName: getDefinedValue(variationData?.fileName, tmplEl.properties?.fileName || ''),
                        offsetTime: variationData?.offsetTime ?? (tmplEl.properties?.value?.offsetTime || 0),
                        isAlpha: variationData?.isAlpha ?? (tmplEl.properties?.value?.isAlpha || false),
                        useAudio: variationData?.useAudio ?? (tmplEl.properties?.useAudio || false),
                        naturalDimension: {
                            width: variationData?.naturalDimension?.width ?? tmplEl.properties?.naturalWidth,
                            height: variationData?.naturalDimension?.height ?? tmplEl.properties?.naturalHeight,
                        },
                        contentTransform: getMergedMediaContentTransform(
                            variationData?.contentTransform,
                            tmplEl.properties?.contentTransform,
                            {
                                width: variationData?.naturalDimension?.width ?? tmplEl.properties?.naturalWidth,
                                height: variationData?.naturalDimension?.height ?? tmplEl.properties?.naturalHeight,
                            },
                            tmplEl,
                            variationData?.srcId !== undefined,
                        ),
                        fadeIn: variationData?.fadeIn ?? (tmplEl.properties?.fadeIn || 0),
                        fadeOut: variationData?.fadeOut ?? (tmplEl.properties?.fadeOut || 0),
                        gain: variationData?.gain ?? (tmplEl.properties?.gain || 0),
                    };
                    break;
                }

                case ElementTypes.AUDIO: {
                    accum[tmplEl.id] = {
                        ...variationData,
                        startFrame: variationData?.startFrame ?? (tmplEl.properties.startFrame || 0),
                        duration: variationData?.duration ?? (tmplEl.properties.duration || 1),
                        hidden: variationData?.hidden ?? (tmplEl.properties?.hidden || false),
                        src: getDefinedValue(variationData?.src, tmplEl.properties?.value?.url || ''),
                        srcId: getDefinedValue(variationData?.srcId, tmplEl.properties?.value?.value || ''),
                        srcType: getDefinedValue(variationData?.srcType, tmplEl.properties?.value?.type),
                        fileName: getDefinedValue(variationData?.fileName, tmplEl.properties?.fileName || ''),
                        offsetTime: variationData?.offsetTime ?? (tmplEl.properties?.value?.offsetTime || 0),
                        fadeIn: variationData?.fadeIn ?? (tmplEl.properties?.fadeIn || 0),
                        fadeOut: variationData?.fadeOut ?? (tmplEl.properties?.fadeOut || 0),
                        gain: variationData?.gain ?? (tmplEl.properties?.gain || 0),
                    };
                    break;
                }

                case ElementTypes.SHAPE: {
                    accum[tmplEl.id] = {
                        ...variationData,
                        startFrame: variationData?.startFrame ?? (tmplEl.properties.startFrame || 0),
                        duration: variationData?.duration ?? (tmplEl.properties.duration || 1),
                        hidden: variationData?.hidden ?? (tmplEl.properties?.hidden || false),
                        fillColor: variationData?.fillColor ?? (tmplEl.properties.fillColor || null),
                        borderColor: variationData?.borderColor ?? (tmplEl.properties.borderColor || null),
                        borderWidth: variationData?.borderWidth ?? (tmplEl.properties.borderWidth || null),
                    };
                    break;
                }

                case ElementTypes.GROUP: {
                    if (!tmplEl.properties?.onlyAllowOneSelection) {
                        break;
                    }

                    accum[tmplEl.id] = {
                        ...variationData,
                        activeDropdownElementId: getDefinedValue(
                            variationData?.activeDropdownElementId,
                            tmplEl.properties?.activeDropdownElementId ?? null,
                        ),
                    };
                    break;
                }

                case ElementTypes.BACKGROUND_COLOR: {
                    accum[tmplEl.id] = { ...variationData, color: variationData?.color ?? tmplEl.properties.value };
                    break;
                }

                case ElementTypes.POSTER_FRAME: {
                    accum[tmplEl.id] = { ...variationData, frame: tmplEl?.properties?.value };
                    break;
                }

                case ElementTypes.GLOBAL_AUDIO: {
                    accum[tmplEl.id] = {
                        ...variationData,
                        startFrame: variationData?.startFrame ?? (tmplEl.properties.startFrame || 0),
                        duration: variationData?.duration ?? (tmplEl.properties.duration || null),
                        src: getDefinedValue(variationData?.src, tmplEl.properties?.value?.url || ''),
                        srcId: getDefinedValue(variationData.srcId, tmplEl.properties?.value?.value || null),
                        srcType: getDefinedValue(variationData?.srcType, tmplEl.properties?.value?.type || null),
                        fileName: getDefinedValue(variationData?.fileName, tmplEl.properties?.fileName || ''),
                        offsetTime: variationData?.offsetTime ?? (tmplEl.properties?.value?.offsetTime || 0),
                        fadeIn: variationData?.fadeIn ?? (tmplEl.properties?.fadeIn || 0),
                        fadeOut: variationData?.fadeOut ?? (tmplEl.properties?.fadeOut || 0),
                        gain: variationData?.gain ?? (tmplEl.properties?.gain || 0),
                    };
                    break;
                }

                case ElementTypes.TRANSCRIPT: {
                    accum[tmplEl.id] = {
                        ...variationData,
                        cues:
                            variationData?.cues ??
                            WebSpecificationParser.parseCuesFromEntries(tmplEl.properties.value?.entries),
                        enabled: variationData?.enabled ?? tmplEl.properties.value?.enabled,
                    };
                    break;
                }
            }

            return accum;
        },
        {},
    );
};

// NOTE: mergeTemplateWithVariation only processes page.elements via flattenTree.
// Transcript lives exclusively in page.globalElements and is merged in parseVariation instead.

const cleanupContentTransform = (obj) => {
    if (!obj) {
        return null;
    }

    return {
        horizontalAlignment: obj.horizontalAlignment || 0,
        verticalAlignment: obj.verticalAlignment || 0,
        horizontalOffset: obj.horizontalOffset || 0,
        verticalOffset: obj.verticalOffset || 0,
        scale: obj.scale || 1,
        type: obj.type,
    };
};

const getComparableObj = (type: ElementTypes, element: any, templateElement: TemplateElement) => {
    let tmpl;
    let prop;

    switch (type) {
        case ElementTypes.BACKGROUND_COLOR: {
            tmpl = { color: templateElement?.properties?.value };
            prop = { color: element.color };
            break;
        }

        case ElementTypes.GLOBAL_AUDIO: {
            tmpl = {
                startFrame: templateElement.properties?.startFrame || 0,
                duration: templateElement.properties?.duration || null,
                src: templateElement.properties?.value?.url || '',
                srcId: templateElement.properties?.value?.value || null,
                srcType: templateElement.properties?.value?.type || null,
                offsetTime: templateElement.properties?.value?.offsetTime || 0,
                fileName: templateElement.properties?.fileName || '',
                gain: templateElement.properties?.gain || 0,
                fadeIn: templateElement.properties?.fadeIn || 0,
                fadeOut: templateElement.properties?.fadeOut || 0,
            };
            prop = {
                startFrame: element.startFrame || 0,
                duration: element.duration || null,
                src: element.src || '',
                srcId: element.srcId || null,
                srcType: element.srcType || null,
                offsetTime: element.offsetTime || 0,
                fileName: element.fileName || '',
                gain: element.gain || 0,
                fadeIn: element.fadeIn || 0,
                fadeOut: element.fadeOut || 0,
            };
            break;
        }

        case ElementTypes.POSTER_FRAME: {
            tmpl = { frame: templateElement?.properties?.value };
            prop = { frame: element.frame };
            break;
        }

        case ElementTypes.TEXT: {
            const defaultProps = SpecificationParser.getTextDefaultProps(templateElement);
            tmpl = {
                startFrame: templateElement.properties.startFrame || 0,
                duration: templateElement.properties.duration || 1,
                hidden: templateElement.properties?.hidden || false,
                formattedText: getStructuredText(templateElement.properties?.value || '', defaultProps),
                textDirection: templateElement.properties?.textDirection || null,
                textBackground: templateElement.properties?.textBackground || null,
                contentTransform: templateElement.properties?.contentTransform || null,
            };
            prop = {
                startFrame: element.startFrame || 0,
                duration: element.duration || 1,
                hidden: element.hidden || false,
                formattedText: element.formattedText,
                textDirection: element.textDirection || null,
                textBackground: element.textBackground?.toObject() || null,
                contentTransform: element.contentTransform?.toObject() || null,
            };
            break;
        }

        case ElementTypes.IMAGE: {
            tmpl = {
                src: cleanS3Url(templateElement.properties?.value?.thumbnail),
                srcId: templateElement.properties?.value?.value,
                srcType: templateElement.properties?.value?.type,
                startFrame: templateElement.properties.startFrame || 0,
                duration: templateElement.properties.duration || 1,
                hidden: templateElement.properties?.hidden || false,
                naturalDimension: {
                    width: templateElement.properties?.naturalWidth,
                    height: templateElement.properties?.naturalHeight,
                },
                contentTransform: cleanupContentTransform(templateElement.properties?.contentTransform),
            };
            prop = {
                src: cleanS3Url(element.src),
                srcId: element.srcId,
                srcType: element.srcType,
                startFrame: element.startFrame || 0,
                duration: element.duration || 1,
                hidden: element.hidden || false,
                naturalDimension: {
                    width: element.naturalDimension.width,
                    height: element.naturalDimension.height,
                },
                contentTransform: cleanupContentTransform(element.contentTransform),
            };
            break;
        }

        case ElementTypes.VIDEO: {
            tmpl = {
                src: cleanS3Url(templateElement.properties?.value?.videoPreviewUrl),
                srcId: templateElement.properties?.value?.value,
                srcType: templateElement.properties?.value?.type,
                startFrame: templateElement.properties?.startFrame || 0,
                offsetTime: templateElement.properties?.value?.offsetTime || 0,
                isAlpha: templateElement.properties?.value?.isAlpha || false,
                useAudio: templateElement.properties?.useAudio || false,
                duration: templateElement.properties?.duration || 1,
                hidden: templateElement.properties?.hidden || false,
                naturalDimension: {
                    width: templateElement.properties?.naturalWidth,
                    height: templateElement.properties?.naturalHeight,
                },
                contentTransform: cleanupContentTransform(templateElement.properties?.contentTransform),
                fadeIn: templateElement.properties?.fadeIn || 0,
                fadeOut: templateElement.properties?.fadeOut || 0,
                gain: templateElement.properties?.gain || 0,
            };
            prop = {
                src: cleanS3Url(element.src),
                srcId: element.srcId,
                srcType: element.srcType,
                startFrame: element.startFrame || 0,
                offsetTime: element.offsetTime,
                isAlpha: element.isAlpha,
                useAudio: element.useAudio,
                duration: element.duration || 1,
                hidden: element.hidden || false,
                naturalDimension: {
                    width: element.naturalDimension.width,
                    height: element.naturalDimension.height,
                },
                contentTransform: cleanupContentTransform(element.contentTransform),
                fadeIn: element.fadeIn,
                fadeOut: element.fadeOut,
                gain: element.gain,
            };
            break;
        }

        case ElementTypes.AUDIO: {
            tmpl = {
                src: cleanS3Url(templateElement.properties?.value?.url),
                srcId: templateElement.properties?.value?.value,
                srcType: templateElement.properties?.value?.type,
                startFrame: templateElement.properties?.startFrame || 0,
                offsetTime: templateElement.properties?.value?.offsetTime || 0,
                duration: templateElement.properties?.duration || 1,
                hidden: templateElement.properties?.hidden || false,
                fadeIn: templateElement.properties?.fadeIn || 0,
                fadeOut: templateElement.properties?.fadeOut || 0,
                gain: templateElement.properties?.gain || 0,
            };
            prop = {
                src: cleanS3Url(element.src),
                srcId: element.srcId,
                srcType: element.srcType,
                startFrame: element.startFrame || 0,
                offsetTime: element.offsetTime,
                duration: element.duration || 1,
                hidden: element.hidden || false,
                fadeIn: element.fadeIn,
                fadeOut: element.fadeOut,
                gain: element.gain,
            };
            break;
        }

        case ElementTypes.SHAPE: {
            tmpl = {
                startFrame: templateElement.properties.startFrame || 0,
                duration: templateElement.properties.duration || 1,
                fillColor: templateElement.properties.fillColor || null,
                borderColor: templateElement.properties.borderColor || null,
                borderWidth: templateElement.properties.borderWidth || null,
                hidden: templateElement.properties.hidden || false,
            };
            prop = {
                startFrame: element?.startFrame || 0,
                duration: element?.duration || 1,
                fillColor: element?.fillColor || null,
                borderColor: element?.borderColor || null,
                borderWidth: element?.borderWidth || null,
                hidden: element?.hidden || false,
            };
            break;
        }

        case ElementTypes.GROUP: {
            tmpl = {
                activeDropdownElementId: templateElement?.properties?.activeDropdownElementId ?? null,
            };
            prop = {
                activeDropdownElementId: element?.activeDropdownElementId ?? null,
            };
            break;
        }

        case ElementTypes.TRANSCRIPT: {
            tmpl = {
                enabled: templateElement?.properties?.enabled ?? false,
                cues: (templateElement?.properties?.value?.entries ?? []).map(stripTranscriptCueId),
            };
            prop = {
                enabled: element?.enabled ?? false,
                cues: (element?.cues ?? []).map(stripTranscriptCueId),
            };
            break;
        }
    }

    return { tmpl, prop };
};

export const getDiffBetweenTemplateAndVariation = (
    template: Template,
    creativeModel: MultiPageImageModel | MultiPageVideoModel,
) => {
    const globalPropertiesDiff = {};
    let disabled = false;
    const pages = [];

    const getDiff = (a, b) => {
        const diff = {};
        Object.keys(a).forEach((key) => {
            if (!equals(a[key], b[key])) {
                diff[key] = a[key];
            }
        });

        return diff;
    };

    const templateElements = flattenTree(template.pages[creativeModel.getCurrentPageIndex()].elements, true) as Record<
        string,
        TemplateElement
    >;

    Object.entries(getGlobalProperties(creativeModel)).forEach(
        ([id, globalProperty]: [id: string, globalProperty: any]) => {
            const templateGlobalProperty = clone(templateElements[id]);
            const { tmpl, prop } = getComparableObj(globalProperty.type, globalProperty, templateGlobalProperty);

            if (!equals(tmpl, prop)) {
                if (globalProperty.type === ElementTypes.GLOBAL_AUDIO) {
                    globalPropertiesDiff[templateGlobalProperty.type] = { id, ...getDiff(tmpl, prop) };
                } else {
                    globalPropertiesDiff[templateGlobalProperty.type] = { id, ...tmpl };
                }
            }
        },
    );

    let hasChanges = Object.keys(globalPropertiesDiff).length > 0;

    const modelsMetaData = creativeModel.getModelsMetaData();
    const models = creativeModel.getModels();
    const currentPageIndex = creativeModel.getCurrentPageIndex();

    const model = models[currentPageIndex];
    const modelMetaData = modelsMetaData[currentPageIndex];
    const pageId = modelMetaData.id;

    const elementDiff: Record<string, Record<string, unknown>> = {};

    model.getAllElementsRecursively().forEach((element) => {
        const templateElement = clone({
            ...templateElements[element.id],
            properties: { ...(templateElements[element.id]?.properties || {}) },
        });

        const { tmpl, prop } = getComparableObj(
            templateElement.type || getElementType(element),
            element,
            templateElement,
        );
        const isMediaElement = [ElementTypes.IMAGE, ElementTypes.VIDEO, ElementTypes.AUDIO].includes(
            templateElement.type,
        );

        if (!equals(tmpl, prop)) {
            elementDiff[element.id] = getDiff(tmpl, prop);

            if (isMediaElement) {
                if ('src' in elementDiff[element.id]) {
                    const { type } = templateElement;
                    const srcPropName =
                        type === ElementTypes.IMAGE
                            ? 'thumbnail'
                            : type === ElementTypes.VIDEO
                              ? 'videoPreviewUrl'
                              : 'url';
                    elementDiff[element.id].src = templateElement.properties?.value?.[srcPropName];
                }
            }
        }
    });

    const elements = Object.entries(elementDiff).map(([id, diff]) => ({ id: Number(id), ...diff }));

    hasChanges = hasChanges || elements.length > 0;

    pages.push({
        index: currentPageIndex,
        pageId,
        elements,
    });

    const isEmptyValue = (value: any) => value === undefined || value === null || value === '';
    const hasEmptyMediaProperty = (el: any) =>
        ('src' in el && isEmptyValue(el.src)) ||
        ('srcId' in el && isEmptyValue(el.srcId)) ||
        ('srcType' in el && isEmptyValue(el.srcType));

    if (elements.some(hasEmptyMediaProperty)) {
        disabled = true;
    }

    return {
        hasChanges,
        disabled,
        globalProperties: globalPropertiesDiff,
        pages,
    };
};

export const isVariationDifferFromTemplate = (
    template: Template,
    creativeModel: MultiPageImageModel | MultiPageVideoModel,
) => {
    const models = creativeModel.getModels();

    return models.some(
        (model: MultiPageImageModel | MultiPageVideoModel | ImageModel | VideoModel, pageIndex: string | number) => {
            const templateElements = flattenTree(template.pages[pageIndex].elements, true) as Record<
                string,
                TemplateElement
            >;
            const hasGlobalChanges = Object.entries(getGlobalProperties(model)).some(
                ([id, globalProperty]: [id: string, globalProperty: any]) => {
                    const templateGlobalProperty = clone(templateElements[id]);
                    const { tmpl, prop } = getComparableObj(
                        globalProperty.type,
                        globalProperty,
                        templateGlobalProperty,
                    );

                    return !equals(tmpl, prop);
                },
            );

            if (hasGlobalChanges) {
                return true;
            }

            return model.getAllElementsRecursively().some((element) => {
                const templateElement = clone({
                    ...templateElements[element.id],
                    properties: { ...(templateElements[element.id]?.properties || {}) },
                });

                const { tmpl, prop } = getComparableObj(
                    templateElement.type || getElementType(element),
                    element,
                    templateElement,
                );

                return !equals(tmpl, prop);
            });
        },
    );
};

export function applyVariationDataToCreativeModel<TModel extends BaseModel>(
    variation,
    creativeModel: MultiPageImageModel | MultiPageVideoModel,
    options?: { skipGlobalAudio?: boolean },
) {
    const models = creativeModel.getModels();
    const modelsMetaData = creativeModel.getModelsMetaData();
    const { skipGlobalAudio = false } = options || {};

    modelsMetaData.forEach(({ id: sizeId }, index) => {
        const globalProperties = getGlobalProperties(models[index]);
        const { elements } = variation?.sizes[sizeId];
        const currentModel = models[index];

        const globalPropertiesToUpdate: {
            GLOBAL_AUDIO_1: any;
            GLOBAL_AUDIO_2: any;
            POSTER_FRAME?: any;
            BACKGROUND_COLOR?: any;
            TRANSCRIPT?: any;
        } = {
            GLOBAL_AUDIO_1: EMPTY_AUDIO_PARAMS,
            GLOBAL_AUDIO_2: EMPTY_AUDIO_PARAMS,
        };

        const updateElementPayload = Object.entries(elements)
            .map(([elId, value]) => {
                if (globalProperties[elId]) {
                    if (globalProperties[elId].type === ElementTypes.GLOBAL_AUDIO && !skipGlobalAudio) {
                        if (globalProperties[elId].name === 'Global audio track 2') {
                            globalPropertiesToUpdate.GLOBAL_AUDIO_2 = value;
                        } else {
                            globalPropertiesToUpdate.GLOBAL_AUDIO_1 = value;
                        }
                    }

                    if (globalProperties[elId].type === ElementTypes.POSTER_FRAME) {
                        globalPropertiesToUpdate.POSTER_FRAME = value;
                    }

                    if (globalProperties[elId].type === ElementTypes.BACKGROUND_COLOR) {
                        globalPropertiesToUpdate.BACKGROUND_COLOR = value;
                    }

                    if (globalProperties[elId].type === ElementTypes.TRANSCRIPT) {
                        globalPropertiesToUpdate.TRANSCRIPT = value;
                    }

                    return null;
                }

                const params = {
                    ...(value as any),
                    id: Number(elId),
                };

                if (
                    params.naturalDimension &&
                    params.naturalDimension.width === 0 &&
                    params.naturalDimension.height === 0
                ) {
                    delete params.naturalDimension;
                }

                return params;
            })
            .filter(Boolean);

        Object.entries(globalPropertiesToUpdate).forEach(([elementType, params]) => {
            const options: UpdateGlobalPropertyOptions = {};
            let resolvedElementType = elementType;

            if (elementType.startsWith('GLOBAL_AUDIO')) {
                options.audioTrackNumber = Number(elementType.substring('GLOBAL_AUDIO_'.length)) as 1 | 2;
                options.skipAssetDestroy = skipGlobalAudio;
                resolvedElementType = ElementTypes.GLOBAL_AUDIO;
            }

            currentModel.updateGlobalProperty(resolvedElementType, params, options);
        });

        currentModel.updateElements(updateElementPayload);
    });
}
